import axios from "axios";
import qs from "qs";
import { Toast, Dialog } from "vant";
import router from "@/router";
import i18n from "@/locales/index";
import { getBrowser } from "@/utils/browser";
import JsBridge from "@/utils/jsBridge";
import vuexStore from "@/store";
import { getAppToken, getRequestHeaderApp } from "@/utils/hybirdMethods";
import {
  getApiHost,
  getApiHostList,
  getPreferredApiHostIndex,
  getMaxApiRetryAttempts,
  getNextApiHostIndex,
  setLastSuccessApiHost,
  logApiDomainConfigOnSuccess
} from "@/utils/apiHost";

function normalizeHost(host?: string): string {
  return (host || "").replace(/\/$/, "");
}

function rememberSuccessApiHost(baseURL?: string) {
  if (baseURL) setLastSuccessApiHost(baseURL);
}
// import Vue from "vue";
// const config = require("../../public/config.json");
// Vue.prototype.$config = config;

import * as Sentry from "@sentry/vue";

const browser = getBrowser();

axios.defaults.timeout = 60000;
axios.defaults.headers.post["Content-Type"] =
  "application/x-www-form-urlencoded;charset=UTF-8";

/** OSS AppConfig 加载完成后同步默认 baseURL */
export function syncAxiosBaseURL() {
  axios.defaults.baseURL = getApiHost();
}

syncAxiosBaseURL();
JsBridge.callHandler("getDeviceInfo", "", (res: any) => {
  // 此页面的接口不需要传这些参数
  if ( window.location.pathname == '/app-pages/accountAnalysis' ) return false
  const data = JSON.parse(res);
  axios.defaults.headers.post["gf-version"] = data["gf-version"];
  axios.defaults.headers.post["gf-device"] = data["gf-device"];
  axios.defaults.headers.post["gf-clientVersion"] = data["gf-clientVersion"];
  axios.defaults.headers.post["gf-network"] = data["gf-network"];
  axios.defaults.headers.post["gf-clientTypes"] = data["gf-clientTypes"];
  axios.defaults.headers.post["gf-system"] = data["gf-system"];
  axios.defaults.headers.post["gf-deviceNo"] = data["gf-deviceNo"];

  axios.defaults.headers.get["gf-version"] = data["gf-version"];
  axios.defaults.headers.get["gf-device"] = data["gf-device"];
  axios.defaults.headers.get["gf-clientVersion"] = data["gf-clientVersion"];
  axios.defaults.headers.get["gf-network"] = data["gf-network"];
  axios.defaults.headers.get["gf-clientTypes"] = data["gf-clientTypes"];
  axios.defaults.headers.get["gf-system"] = data["gf-system"];
  axios.defaults.headers.get["gf-deviceNo"] = data["gf-deviceNo"];
});

// let pending: any = []; //声明一个数组用于存储每个ajax请求的取消函数和ajax标识
// 声明一个 Map 用于存储每个请求的标识 和 取消函数
const pending = new Map();

// let cancelToken = axios.CancelToken;

/**
 * 添加请求
 * @param {Object} config
 */
const addPending = config => {
  const url = [
    config.method,
    config.url,
    qs.stringify(config.params),
    qs.stringify(config.data)
  ].join("&");
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken(cancel => {
      if (!pending.has(url)) {
        // 如果 pending 中不存在当前请求，则添加进去
        pending.set(url, cancel);
      }
    });
};

/**
 * 移除请求
 * @param {Object} config
 */
const removePending = config => {
  const url = [
    config.method,
    config.url,
    qs.stringify(config.params),
    qs.stringify(config.data)
  ].join("&");
  if (pending.has(url)) {
    // 如果在 pending 中存在当前请求标识，需要取消当前请求，并且移除
    const cancel = pending.get(url);
    cancel(url);
    pending.delete(url);
  }
};

/**
 * 清空 pending 中的请求（在路由跳转时调用）
 */
export const clearPending = () => {
  for (const [url, cancel] of pending) {
    cancel(url);
  }
  pending.clear();
};

let needLoadingRequestCount = 0;

let loadingText = "";
let toast: any = "";

export function showFullScreenLoading() {
  if (needLoadingRequestCount === 0) {
    toast = Toast.loading({
      // mask: true, //蒙层
      duration: 0, // 持续展示 toast
      forbidClick: true, // 禁用背景点击
      loadingType: "spinner",
      message: loadingText
    });
  }
  needLoadingRequestCount++;
}

export function tryHideFullScreenLoading() {
  if (needLoadingRequestCount <= 0) return;
  needLoadingRequestCount--;
  if (needLoadingRequestCount === 0) {
    Toast.clear();
  }
}

function handleErrorCode(err) {
  if (err && err.response) {
    switch (err.response.status) {
      case 400:
        err.message = i18n.t('network_error_text.t00001');
        break;
      case 401:
        err.message = i18n.t('network_error_text.t00002');
        break;
      case 403:
        err.message = i18n.t('network_error_text.t00003');
        break;
      case 404:
        err.message = i18n.t('network_error_text.t00004');
        break;
      case 405:
        err.message = i18n.t('network_error_text.t00005');
        break;
      case 408:
        err.message = i18n.t('network_error_text.t00006');
        break;
      case 500:
        err.message = i18n.t('network_error_text.t00007');
        break;
      case 501:
        err.message = i18n.t('network_error_text.t00008');
        break;
      case 502:
        err.message = i18n.t('network_error_text.t00009');
        break;
      case 503:
        err.message = i18n.t('network_error_text.t00010');
        break;
      case 504:
        err.message = i18n.t('network_error_text.t00011');
        break;
      case 505:
        err.message = i18n.t('network_error_text.t00012');
        break;
      default:
        err.message = `${i18n.t('network_error_text.t00013')}${err.response.status}`;
    }
    const errData = {
      code: err.response.status,
      message: err.message
    };
    Toast.fail(err.message);
    // 统一错误处理可以放这，例如页面提示错误...
    // 
  }
}

function getAuthToken(): string | null {
  const isH5Page = router.currentRoute.meta.isH5Page;
  const sessionToken = sessionStorage.getItem("token");
  const localToken = localStorage.getItem("token");
  if (isH5Page) {
    return localToken || sessionToken;
  }
  return sessionToken || localToken;
}

axios.interceptors.request.use(
  async (config: any) => {
    config.time = {
      startTime: Date.now()
    };

    if (!(config as any).customLoading && !router.currentRoute.meta.isH5Page) {
      if (toast && toast.clear) {
        toast.clear();
      }
      showFullScreenLoading();
    }
    if (router.currentRoute.meta.isH5Page) {
      tryHideFullScreenLoading();
    }

    // removePending(config); // 在请求开始前，对之前的请求做检查取消操作
    // addPending(config); // 将当前请求添加到 pending 中
    // other code before request

    let token = getAuthToken();

    await getAppToken();
    token = getAuthToken();
    token && (config.headers.Authorization = `Bearer ${token}`);
    config.headers["accept-language"] = i18n.locale;

    if (browser.isApp) {
      try {
        const appRequestHead: any = await getRequestHeaderApp();
        const needKey = [
          "platform",
          "gf-deviceNo",
          "gf-device",
          "gf-version",
          "gf-system",
          "gf-clientVersion",
          "gf-clientTypes",
          "gf-deviceName"
        ];
        if ( window.location.pathname == '/app-pages/accountAnalysis' ) return config
        for (const key in appRequestHead) {
          if (needKey.includes(key) && appRequestHead[key]) {
            config.headers[key] = appRequestHead[key];
          }
        }
      } catch (e) {}
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

import { submitApiInfoToSentry } from "@/utils/customSentry.js";

let poorNetworkInterval: any = 0; // 弱网-定时器
let poorNetworkTimeout: any = -1; // 正常网络-定时器
axios.interceptors.response.use(
  (response: any) => {
    // // 修改一下最初的默认值为 重试的那个 并把默认的那个 加到备选里面去 start
    if (response.config.baseURL) {
      const successHost = normalizeHost(response.config.baseURL);
      axios.defaults.baseURL = successHost;
      (window as any).$config.VUE_APP_API_HOST = successHost;
      rememberSuccessApiHost(successHost);
    }
    // // 修改一下最初的默认值为 重试的那个 并把默认的那个 加到备选里面去 end

    const endTime = Date.now();
    const responseTime = endTime - response.config.time.startTime;
    response.time = {
      startTime: response.config.time.startTime,
      endTime,
      responseTime
    };

    // 在不影响业务正常运行的情况下，上报接口信息至Sentry。
    // try {
    //   // submitApiInfoToSentry(response);
    // } catch (err) {
    //   // 
    // }

    // 弱网环境
    if (responseTime > 1000) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      poorNetCounterIncrement();
    }
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    poorNetJudge();

    tryHideFullScreenLoading();
    removePending(response); // 在请求结束后，移除本次请求
    const config: any = response.config;
    const { hideFailMessage } = config;
    let { code, data, msg, error_code } = response.data;
    // 
    
    const obj: any = router.currentRoute.meta;
    const isAuthFailCode =
      code == 123 || code == 137 || code == "137" || code == 100019;

    if (obj.authFailToastOnly && isAuthFailCode) {
      msg && Toast(msg);
      return Promise.reject(response.data);
    }

    if (obj.isH5Axios) {
      if (response.data.code == 123 || response.data.code == 100019) {
        localStorage.removeItem("token"); // 登录失效 清除token
        localStorage.removeItem("h5userInfo"); // 登录失效 清除用户信息
        try {
          Sentry.configureScope(scope => {
            scope.setUser({});
          });
        } catch (err) {
          // ignore
        }
        router.push({ name: "h5PageLogin" }).catch(err => {});
        return false;
      }
      const h5Data = response.data;
      if (h5Data && (h5Data.code == 0 || h5Data.error_code === 0)) {
        logApiDomainConfigOnSuccess();
      }
      return h5Data;
    }
    // if(obj.isH5Axios && response.data.error_code){
    // 	if(response.data.error_code !== 0){
    // 		return response.data
    // 	}else{
    // 		return response
    // 	}
    // }else{
    // 	return response.data.data
    // }

    // 
    if (code == 123) {
      if (obj && obj.isH5Page) {
        router.push({ name: "h5PageLogin" }).catch(err => {});
      } else {
        browser.isIdcApp
          ? router.push({ name: "loginH5" }).catch(err => {})
          : router.push("/login").catch(err => {});
      }

      return Promise.resolve();
    }
    // @ts-ignore
    if (code == "137" || code == 100019) {
      Dialog.alert({
        ["title" as any]: i18n.t("common.tips"),
        message: msg,
        ["className" as any]: document.getElementsByClassName("black-theme")[0]
          ? "black-theme-dialog"
          : ""
      }).then(() => {
        if (obj && obj.isH5Page) {
          router.push({ name: "h5PageLogin" }).catch(err => {});
        } else {
          browser.isIdcApp
            ? router.push({ name: "loginH5" }).catch(err => {})
            : router.push("/login").catch(err => {});
        }
      });
      return Promise.resolve(msg);
    }

    if (code === 100173 || code === 100174) {
      return response.data;
    }

    if (code == 0) {
      logApiDomainConfigOnSuccess();
      notifySuccess(response);
      return data;
    } else {
      if (msg) {
        !hideFailMessage && Toast(msg);
        return Promise.reject(response.data);
      } else {
        return response;
      }
    }
  },
  error => {
    const { config } = error;
    if (!config) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return axiosResponseError(error);
    }

    const retryBaseURLList = getApiHostList().map(normalizeHost);
    if (!retryBaseURLList.length) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return axiosResponseError(error);
    }

    const maxAttempts = getMaxApiRetryAttempts(retryBaseURLList);
    const currentIndex =
      typeof config.__apiHostIndex === "number"
        ? config.__apiHostIndex
        : getPreferredApiHostIndex();
    config.__apiRetryAttempt = (config.__apiRetryAttempt || 0) + 1;

    if (config.__apiRetryAttempt >= maxAttempts) {
      Sentry.captureMessage("API_RETRY_ERROR", {
        level: "info",
        extra: {
          url: config.url,
          lastBaseURL: retryBaseURLList[currentIndex],
          retryBaseURLList,
          maxAttempts,
          retryAttempt: config.__apiRetryAttempt,
          date: String(new Date())
        }
      });
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return axiosResponseError(error);
    }

    const nextIndex = getNextApiHostIndex(
      currentIndex,
      retryBaseURLList.length
    );
    const lastBaseURL = retryBaseURLList[currentIndex];
    const nextBaseURL = retryBaseURLList[nextIndex];
    config.__apiHostIndex = nextIndex;
    config.__retryCount = config.__apiRetryAttempt;
    config.baseURL = nextBaseURL;
    config.retryInfo = {
      index: nextIndex,
      lastBaseURL,
      retryBaseURL: nextBaseURL,
      retryCount: config.__apiRetryAttempt,
      maxAttempts,
      keyCn:
        "index:当前host下标;lastBaseURL:失败的host;retryBaseURL:重试host;maxAttempts:本轮最多尝试次数;Date:时间;",
      date: String(new Date())
    };

    clearTimeout(config.__retryTimer);
    const backoff = new Promise<void>(function(resolve) {
      config.__retryTimer = setTimeout(function() {
        resolve();
      }, config.retryDelay || 400);
    });
    return backoff.then(function() {
      return axios(config);
    });
  }
);

const notifySuccess = response => {
  const successMessages = {
    "/php/api/group/account/add": true
  };
  if (successMessages[response.config.url]) {
    if (response.data && response.data.msg) {
      Toast({
        message: response.data.msg,
        duration: 3000
      });
    }
  }
};

const axiosResponseError: any = (error: any) => {
  // 没有收到响应，可能是网络断开或请求超时
  if (!error.response) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    poorNetCounterIncrement();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    poorNetJudge();
  }

  tryHideFullScreenLoading();
  if (!axios.isCancel(error)) {
    const hideFailMessage = error.config && error.config.hideFailMessage;
    if (!hideFailMessage) {
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        Toast.fail("网络超时");
      }
      if (error.message.includes("Network Error")) {
        Toast.fail("网络错误");
      }
      handleErrorCode(error);
    }
  }
  return Promise.reject(error);
};

function fetch(params) {
  const { url, method, data, baseURL, ...config } = params;
  const resolvedBaseURL = normalizeHost(baseURL || getApiHost());
  const hostList = getApiHostList().map(normalizeHost);
  const hostIndex = hostList.indexOf(resolvedBaseURL);
  const options = {
    url,
    method,
    baseURL: resolvedBaseURL,
    __apiHostIndex: hostIndex >= 0 ? hostIndex : getPreferredApiHostIndex(),
    __apiRetryAttempt: 0,
    [method == "get" ? "params" : "data"]: data,
    ...config
  };
  loadingText = config.loadingText || i18n.t("common.loading_text");

  return new Promise((resolve, reject) => {
    axios(options)
      .then(response => {
        resolve(response);
      })
      .catch(err => {
        reject(err);
      });
  });
}

/**
 * @Description 对异常网络判断的封装
 * @date 2023/12/20
 * @return {void}
 */
function poorNetJudge() {
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  if (!vuexStore.state.network.v_poor_network_environment_status) {
    // 是否为 正常网络
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    if (vuexStore.state.network.v_poor_network_environment_counter >= 5) {
      // 请求 超过一定时间间隔的 次数 >=5,从 正常网络 变更 弱网
      clearTimeout(poorNetworkTimeout); // 清除 正常网络-定时器
      poorNetworkTimeout = -1; // 清除 正常网络-定时器

      vuexStore.commit("network/judge_poor_network_environment_status", true); // 判断弱网状态：是弱网true
      vuexStore.commit("network/reset_poor_network_environment_counter"); // 重置弱网计数器：0

      // 弱网-定时器
      poorNetworkInterval = setInterval(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        if (vuexStore.state.network.v_poor_network_environment_counter < 5) {
          // 请求 超过一定时间间隔的 次数 <5,从 弱网 变更 正常网络
          vuexStore.commit(
            "network/judge_poor_network_environment_status",
            false
          ); // 判断弱网状态：不是弱网false
          clearInterval(poorNetworkInterval); // 清除 弱网-定时器
        }
        vuexStore.commit("network/reset_poor_network_environment_counter"); // 重置弱网计数器：0
      }, 60000);
    }
  }
}

/**
 * @Description 异常网络计数器增加
 * @date 2023/12/26
 * @return {void}
 */
function poorNetCounterIncrement() {
  vuexStore.commit("network/add_poor_network_environment_counter");
  // @ts-ignore
  if (!vuexStore.state.network.v_poor_network_environment_status) {
    // 正常网络
    if (poorNetworkTimeout === -1) {
      // 正常网络下，第一次出现超时请求时，就开始计时60s。
      poorNetworkTimeout = setTimeout(function() {
        vuexStore.commit("network/reset_poor_network_environment_counter"); // 重置弱网计数器：0
        poorNetworkTimeout = -1;
      }, 60000);
    }
  }
}

export default fetch;
